import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { reportUnreadableSavedState } from '@/shared/lib/unreadableSavedState';

export type RailKey =
  | 'structure'
  | 'models'
  | 'moduleLibrary'
  | 'catalog'
  | 'cabinetTemplates'
  | 'kitchenConstructor'
  | 'orders'
  | 'materials'
  | 'hardware'
  | 'ingest'
  | 'facades'
  | 'drawers'
  | 'constructive'
  | 'fill'
  | 'layoutTools'
  | 'properties'
  | 'estimate'
  | 'analysis'
  | 'renders'
  | 'history'
  | 'furnitureList'
  | 'annotations'
  | 'chat'
  | 'electrical'
  | 'knowledge';

export type Theme = 'dark' | 'light';
type WorkMode = 'design' | 'presentation' | 'qc';
export type AppMode = 'scene' | 'plan' | 'analysis' | 'verify' | 'estimate' | 'drawings' | 'modules' | 'params';

/**
 * Top-level workspace tabs.  Each is a self-contained AРМ:
 *   · 'scene'  — 3D Viewer (designer / sales)
 *   · 'tech'   — АРМ Технолог: единая БД материалов + операций + сметы + раскроя +
 *                брифов + ревизий.  Технолог работает в одном АРМ — материалы и
 *                смета это разделы одного рабочего места, а не отдельные вкладки.
 *   · 'orders' — CRM (managers)
 * The simple Смета flyout INSIDE 'scene' continues to exist as a read-mostly
 * preview — switching to 'tech' (section=smeta) opens the full editor.
 */
export type Workspace = 'scene' | 'tech' | 'orders' | 'models';

/**
 * Sections inside the АРМ Технолог workspace.  Selected via the left nav
 * inside the tech surface, not via the top-bar tabs.
 */
export type TechSection =
  | 'materials'
  | 'operations'
  | 'smeta'
  | 'cut'
  | 'production'
  | 'advisor'
  | 'review'
  | 'library-review'
  | 'pricebook'
  | 'briefs'
  | 'revisions';

export type BulkDialogTab = 'material' | 'edges';
export type ActiveSmetaOrder = { orderId: string; modelId: string; title: string;
  revisionId:string; revisionIndex:number };
type BulkDialogState = {
  ids: string[];
  tab: BulkDialogTab;
} | null;

type UiState = {
  theme: Theme;
  workMode: WorkMode;
  appMode: AppMode;
  workspace: Workspace;
  techSection: TechSection;
  leftRail: RailKey | null;
  rightRail: RailKey | null;
  panelDetailId: string | null;
  /** entity id of module being edited in ModuleModal — null = closed */
  moduleModalId: string | null;
  /** entity id of composition being edited in CompositionModal — null = closed */
  compositionModalId: string | null;
  /** PDF-import pipeline modal (ScenePipelinePanel) — hosted in GlobalModalHost */
  scenePipelineOpen: boolean;
  /**
   * Поверхность сцены сама несёт согласованные рельсы `prototype-navigation`
   * («Что строим» слева, «Чем смотрим» справа).  Пока это так, оболочка НЕ
   * рисует свои орбиты: замер 21.09.2026 на 1680 px показал два набора рельс
   * друг поверх друга — `prototype-navigation` (x=0, ш=208) и
   * `luminous-tool-orbit` (x=18, ш=152), 34 кнопки слева и 28 справа разом.
   * Флаг НЕ сохраняется: он про открытую сцену, а не про настройку человека.
   */
  surfaceOwnsRails: boolean;
  bulkDialog: BulkDialogState;
  /** Server-verified order context; never authority for model/Smeta lookup. */
  activeSmetaOrder: ActiveSmetaOrder | null;
  /** (#7) Library favorites — most-recently used catalog ids (LRU-ordered). */
  recentCatalogIds: string[];
  pushRecentCatalogId: (id: string) => void;
  clearRecentCatalogIds: () => void;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setWorkMode: (m: WorkMode) => void;
  setAppMode: (m: AppMode) => void;
  setWorkspace: (w: Workspace) => void;
  setTechSection: (s: TechSection) => void;
  openLeft: (k: RailKey) => void;
  openRight: (k: RailKey) => void;
  closeLeft: () => void;
  closeRight: () => void;
  toggleLeft: (k: RailKey) => void;
  toggleRight: (k: RailKey) => void;
  openPanelDetail: (id: string) => void;
  closePanelDetail: () => void;
  openModuleModal: (id: string | null) => void;
  openCompositionModal: (id: string | null) => void;
  openScenePipeline: () => void;
  closeScenePipeline: () => void;
  setSurfaceOwnsRails: (owns: boolean) => void;
  openBulkDialog: (ids: string[], tab?: BulkDialogTab) => void;
  closeBulkDialog: () => void;
  setActiveSmetaOrder: (context: ActiveSmetaOrder) => void;
  clearActiveSmetaOrder: () => void;
};

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      workMode: 'design',
      appMode: 'scene',
      workspace: 'scene',
      techSection: 'materials',
      // Дефолт = закрытая левая рука: с открытой панелью вход в режимы сцены
      // скрыт (фикс клик-стилинга 01.10), и свежий посетитель не видел ни
      // одного способа сменить режим. Persist у вернувшихся сохраняет их
      // осознанный выбор; sanitization (:208+) чистит битые ключи.
      leftRail: null,
      rightRail: null,
      panelDetailId: null,
      moduleModalId: null,
      compositionModalId: null,
      scenePipelineOpen: false,
      surfaceOwnsRails: false,
      bulkDialog: null,
      activeSmetaOrder: null,
      recentCatalogIds: [],
      pushRecentCatalogId: (id) => {
        if (!id) return;
        const cur = get().recentCatalogIds || [];
        const next = [id, ...cur.filter((x) => x !== id)].slice(0, 20);
        set({ recentCatalogIds: next });
      },
      clearRecentCatalogIds: () => set({ recentCatalogIds: [] }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
      setWorkMode: (workMode) => set({ workMode }),
      setAppMode: (appMode) =>
        // The left rail holds scene-authoring tools (наполнение, фасады, …) that
        // only act on the 3D model. Full-width report views (смета/анализ/чертежи)
        // have nothing to drag a fill onto, so collapse the flyout there instead
        // of leaving a dead authoring panel beside the report. Scene + Модули keep it.
        set(
          appMode === 'estimate' || appMode === 'analysis' || appMode === 'drawings'
            ? { appMode, leftRail: null }
            : { appMode },
        ),
      // Workspace navigation is the canonical top-level mode owner. Entering
      // Scene always means the live canvas, never whichever full-screen scene
      // report happened to be open before Tech/Orders navigation. Keep both
      // fields in one Zustand update so header, ribbon and main surface cannot
      // observe different navigation states.
      setWorkspace: (workspace) => set(
        workspace === 'scene' ? { workspace, appMode: 'scene' } : { workspace },
      ),
      setTechSection: (techSection) => set({ techSection }),
      openLeft: (leftRail) => set({ leftRail }),
      openRight: (rightRail) => set({ rightRail }),
      closeLeft: () => set({ leftRail: null }),
      closeRight: () => set({ rightRail: null }),
      toggleLeft: (k) => set({ leftRail: get().leftRail === k ? null : k }),
      toggleRight: (k) => set({ rightRail: get().rightRail === k ? null : k }),
      openPanelDetail: (panelDetailId) => set({ panelDetailId }),
      closePanelDetail: () => set({ panelDetailId: null }),
      openModuleModal: (moduleModalId) => set({ moduleModalId }),
      openCompositionModal: (compositionModalId) => set({ compositionModalId }),
      setSurfaceOwnsRails: (surfaceOwnsRails) => set((state) => (
        state.surfaceOwnsRails === surfaceOwnsRails ? state : { surfaceOwnsRails }
      )),
      openScenePipeline: () => set({ scenePipelineOpen: true }),
      closeScenePipeline: () => set({ scenePipelineOpen: false }),
      openBulkDialog: (ids, tab = 'material') => set({ bulkDialog: { ids: [...new Set(ids)], tab } }),
      closeBulkDialog: () => set({ bulkDialog: null }),
      setActiveSmetaOrder: (activeSmetaOrder) => set({ activeSmetaOrder }),
      clearActiveSmetaOrder: () => set({ activeSmetaOrder: null }),
    }),
    {
      name: 'holzcom-ui',
      storage: createJSONStorage(() => localStorage),
      version: 8,
      // Непрочитанное сохранённое состояние (порченый localStorage, упавшая migrate()) НЕ
      // проглатывается тихо в дефолтную раскладку — см. unreadableSavedState.ts.
      onRehydrateStorage: () => (_state, error) => {
        if (error) reportUnreadableSavedState('раскладка панелей', error);
      },
      migrate: (persisted: unknown, fromVersion: number) => {
        // Versions <2 didn't know about the 'moduleLibrary' / 'kitchenConstructor'
        // rail keys. Drop any stale leftRail value that referenced a missing
        // key — fall back to null (rail closed) so the new ITEMS list renders.
        if (fromVersion < 2 && persisted && typeof persisted === 'object') {
          const obj = persisted as Record<string, unknown>;
          const valid: ReadonlySet<RailKey | null> = new Set<RailKey | null>([
            null,
            'structure',
            'models',
            'moduleLibrary',
            'catalog',
            'kitchenConstructor',
            'orders',
            'materials',
            'hardware',
            'ingest',
            'facades',
            'drawers',
            'constructive',
            'fill',
            'layoutTools',
            'cabinetTemplates',
            'properties',
            'estimate',
            'analysis',
            'renders',
            'history',
            'furnitureList',
            'annotations',
            'chat',
            'electrical',
            'knowledge',
          ]);
          if (!valid.has(obj.leftRail as RailKey | null)) obj.leftRail = null;
          if (!valid.has(obj.rightRail as RailKey | null)) obj.rightRail = null;
        }
        // v3: introduce 'workspace' tab. Default to 'scene' for existing users.
        if (fromVersion < 3 && persisted && typeof persisted === 'object') {
          const obj = persisted as Record<string, unknown>;
          if (typeof obj.workspace !== 'string') obj.workspace = 'scene';
        }
        // v4: merge АРМ Материалы + АРМ Технолог-Смета into a single 'tech'
        // workspace.  Anyone who had one of the old values lands on tech with
        // the matching left-nav section preselected.
        if (fromVersion < 4 && persisted && typeof persisted === 'object') {
          const obj = persisted as Record<string, unknown>;
          if (obj.workspace === 'materials') {
            obj.workspace = 'tech';
            obj.techSection = 'materials';
          } else if (obj.workspace === 'tech_smeta') {
            obj.workspace = 'tech';
            obj.techSection = 'smeta';
          }
          const validWs = new Set<string>(['scene', 'tech', 'orders', 'models']);
          if (typeof obj.workspace !== 'string' || !validWs.has(obj.workspace as string)) {
            obj.workspace = 'scene';
          }
          if (typeof obj.techSection !== 'string') obj.techSection = 'materials';
        }
        if (fromVersion < 7 && persisted && typeof persisted === 'object') {
          (persisted as Record<string, unknown>).activeSmetaOrder = null;
        }
        if (fromVersion < 8 && persisted && typeof persisted === 'object') {
          (persisted as Record<string, unknown>).theme = 'light';
        }
        // v5 adds the model-chat and knowledge-base flyouts to the persisted
        // rail union. Existing valid state needs no data rewrite.
        return persisted as Partial<UiState>;
      },
      partialize: (state) => ({
        theme: state.theme,
        workMode: state.workMode,
        appMode: state.appMode,
        workspace: state.workspace,
        techSection: state.techSection,
        leftRail: state.leftRail,
        rightRail: state.rightRail,
        activeSmetaOrder: state.activeSmetaOrder,
      }),
    },
  ),
);
